import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, CheckCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const READING_OPTIONS = [
  {
    value: 'no',
    sv: 'Nej, jag är helt nybörjare',
    en: 'No, I am a complete beginner',
    desc_sv: 'Jag har aldrig läst arabiska bokstäver.',
    desc_en: 'I have never read Arabic letters.'
  },
  {
    value: 'slow',
    sv: 'Ja, men jag läser långsamt',
    en: 'Yes, but I read slowly',
    desc_sv: 'Jag kan läsa arabiska bokstäver men behöver öva mer.',
    desc_en: 'I can read Arabic letters but need more practice.'
  },
  {
    value: 'yes',
    sv: 'Ja, jag kan läsa Koranen',
    en: 'Yes, I can read Quran',
    desc_sv: 'Jag kan läsa Koranen och vill förbättra min recitation.',
    desc_en: 'I can read the Quran and want to improve my recitation.'
  }
];

const LEVEL_LABELS = {
  sv: { beginner: 'Nybörjare — Quran Online Method', intermediate: 'Medel — Tajweed', advanced: 'Avancerad — Hifdh' },
  en: { beginner: 'Beginner — Quran Online Method', intermediate: 'Intermediate — Tajweed', advanced: 'Advanced — Hifdh' }
};

const PlacementTestPage = () => {
  const { token } = useParams();
  const { language } = useLanguage();
  const sv = language === 'sv';
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/placement-test/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setTestData(d); setLoading(false); })
      .catch(() => { setError(sv ? 'Testet hittades inte.' : 'Test not found.'); setLoading(false); });
  }, [token, sv]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/placement-test/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ can_read_arabic: selected })
      });
      const data = await res.json();
      if (res.ok) setResult(data);
      else setError(data.detail || 'Error');
    } catch {
      setError(sv ? 'Anslutningsfel' : 'Connection error');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-muted)' }}>Laddar...</div>;
  if (error) return (
    <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="pricing-card text-center max-w-md mx-4"><p className="text-red-400">{error}</p></div>
    </div>
  );

  if (testData?.completed || result) {
    const level = result?.recommended_level || testData?.recommended_level;
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="placement-test-completed">
        <div className="pricing-card text-center max-w-md mx-4">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-500/10 border border-green-500/30">
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Tack!' : 'Thank you!'}
          </h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
            {sv ? 'Ditt test är inskickat. Din lärare kommer att utvärdera din nivå under provlektionen.' : 'Your test is submitted. Your teacher will evaluate your level during the trial lesson.'}
          </p>
          {level && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
              <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Rekommenderad nivå:' : 'Recommended level:'}</p>
              <p className="text-gold font-semibold">{LEVEL_LABELS[language]?.[level] || level}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="placement-test-page">
      <section className="py-12 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm text-gold mb-6" style={{ border: '1px solid rgba(206, 150, 8, 0.3)' }}>
            <BookOpen size={16} />
            {sv ? 'Nivåtest' : 'Placement Test'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'Välkommen' : 'Welcome'}{testData?.student_name ? `, ${testData.student_name}` : ''}!
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-xl mx-auto">
            {sv ? 'Svara på frågan nedan så att vi kan placera dig i rätt kursgrupp.' : 'Answer the question below so we can place you in the right course group.'}
          </p>
        </div>
      </section>
      <div className="gold-divider"></div>
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="pricing-card !p-8">
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Kan du läsa arabiska bokstäver?' : 'Can you read Arabic letters?'}
            </h2>
            <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
              {sv ? 'Välj det alternativ som bäst beskriver din nuvarande nivå.' : 'Choose the option that best describes your current level.'}
            </p>
            <div className="space-y-3 mb-8">
              {READING_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-4 p-5 rounded-lg cursor-pointer transition-all ${selected === opt.value ? 'border-gold' : ''}`}
                  style={{
                    border: selected === opt.value ? '2px solid var(--gold)' : '1px solid var(--border-color)',
                    backgroundColor: selected === opt.value ? 'rgba(206, 150, 8, 0.05)' : 'transparent'
                  }}
                  data-testid={`option-${opt.value}`}
                >
                  <input type="radio" name="reading" value={opt.value} checked={selected === opt.value} onChange={() => setSelected(opt.value)} className="mt-1" />
                  <div>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{sv ? opt.sv : opt.en}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{sv ? opt.desc_sv : opt.desc_en}</p>
                  </div>
                </label>
              ))}
            </div>
            <button
              onClick={handleSubmit}
              disabled={!selected || submitting}
              className="btn-gold w-full flex items-center justify-center gap-2 text-lg py-4 disabled:opacity-50"
              data-testid="submit-placement-test"
            >
              {submitting ? '...' : (sv ? 'Skicka svar' : 'Submit Answer')} {!submitting && <ArrowRight size={20} />}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlacementTestPage;
